import React, {
    ReactElement,
    ReactNode,
    createContext,
    useCallback,
    useMemo,
    useState,
} from "react"

import { LCTOnlyDatasets } from "../../../../../../constants/enums"
import { AppError } from "../../../../../../errors/errorProcessors"
import usePublishLCTDevice from "../../../../../../services/middleware/hooks/devices/lct/usePublishLCTDevice"
import useSaveLCTDeviceDraft from "../../../../../../services/middleware/hooks/devices/lct/useSaveLCTDeviceDraft"
import { checkContextErrors } from "../../../../../../utils/registerDevice/registerDeviceUtils"
import { IDeviceFormContextValue } from "../../../../types/providerTypes"
import { HpDeviceFormData } from "../../../types/providerTypes"

type CreateHpDeviceProviderProps = {
    formData: HpDeviceFormData
    setFormData: React.Dispatch<React.SetStateAction<HpDeviceFormData>>
    formDataIsLoading: boolean
    formDataError?: AppError
    systemReference?: string
    attachmentUUID?: string
    canEditManufacturer: boolean
    dataset?: LCTOnlyDatasets
    deviceContext?: React.Context<IDeviceFormContextValue<HpDeviceFormData>>
    children: ReactNode
}

export const CreateHpDeviceContext = createContext(
    {} as IDeviceFormContextValue<HpDeviceFormData>
)

const CreateHpDeviceProvider = ({
    formData,
    setFormData,
    formDataIsLoading,
    formDataError,
    attachmentUUID,
    canEditManufacturer,
    systemReference,
    dataset = LCTOnlyDatasets.HP,
    deviceContext = CreateHpDeviceContext,
    children,
}: CreateHpDeviceProviderProps): ReactElement => {
    const [currentIndex, setCurrentIndex] = useState<number>(0)
    const [deviceRef, setDeviceRef] = useState<string | undefined>()
    const [errors, setErrors] = useState<AppError[]>([])

    const {
        publishLCTDevice,
        error: publishError,
        isLoaded: publishSuccessful,
        isLoading: publishInProgress,
    } = usePublishLCTDevice(formData, dataset, attachmentUUID, systemReference)

    const {
        saveLCTDeviceDraft,
        isLoading: saveDraftInProgress,
        isLoaded: saveDraftSuccessful,
    } = useSaveLCTDeviceDraft<HpDeviceFormData>(
        false,
        dataset,
        systemReference,
        attachmentUUID
    )

    checkContextErrors([formDataError, publishError], errors, (error) =>
        setErrors((prev) => [...prev, error])
    )
    const setIndex = (step: number): void => {
        setErrors([])
        setCurrentIndex(step)
    }

    const onSubmit = useCallback(async () => {
        const res = await publishLCTDevice()
        setDeviceRef(res?.deviceRef)
    }, [publishLCTDevice, setDeviceRef])

    const value: IDeviceFormContextValue<HpDeviceFormData> = useMemo(
        () => ({
            formData,
            setFormData,
            currentIndex,
            setCurrentIndex: setIndex,
            onSubmit,
            submitLoading: publishInProgress,
            submitSuccessful: publishSuccessful,
            canEditManufacturer,
            formIsLoading: formDataIsLoading,
            saveDraft: saveLCTDeviceDraft,
            saveDraftLoading: saveDraftInProgress,
            saveDraftSuccessful,
            deviceRef,
            attachmentUUID,
            errors,
        }),
        [
            formData,
            setFormData,
            currentIndex,
            onSubmit,
            publishInProgress,
            publishSuccessful,
            canEditManufacturer,
            formDataIsLoading,
            saveLCTDeviceDraft,
            saveDraftInProgress,
            saveDraftSuccessful,
            deviceRef,
            attachmentUUID,
            errors,
        ]
    )

    const ContextProvider = deviceContext.Provider

    return <ContextProvider value={value}>{children}</ContextProvider>
}

CreateHpDeviceProvider.defaultProps = {
    formDataError: undefined,
    attachmentUUID: undefined,
    systemReference: undefined,
}

export default CreateHpDeviceProvider
